import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { ALL_SYMBOLS, subscribeMultiTicker } from '../../lib/market';
import { useToast } from '../../hooks/use-toast';
import { Activity, TrendingUp, TrendingDown, Search, X, Save, RefreshCw } from 'lucide-react';

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(v) ? '—' : Number(v).toFixed(d));
const money = (v) => (v === null || v === undefined || Number.isNaN(v) ? '—' : `${v >= 0 ? '+' : '-'}$${Math.abs(v).toFixed(2)}`);

function useLivePrices(symbols) {
  const [prices, setPrices] = useState({});
  const key = symbols.slice().sort().join(',');
  useEffect(() => {
    const subs = key.split(',').filter(Boolean).map(s => ALL_SYMBOLS.find(a => a.symbol === s)).filter(Boolean);
    if (subs.length === 0) { return undefined; }
    const ws = subscribeMultiTicker(subs, (t) => {
      if (t?.last == null || Number.isNaN(t.last)) { return; }
      setPrices(prev => ({ ...prev, [t.symbol]: t.last }));
    });
    return () => { try { ws.close(); } catch {} };
  }, [key]);
  return prices;
}

const ActionDialog = ({ dlg, onClose, onDone }) => {
  const { toast } = useToast();
  const p = dlg.pos;
  const [sl, setSl] = useState(p.sl || '');
  const [tp, setTp] = useState(p.tp || '');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const isEdit = dlg.kind === 'edit';
  const isWin = dlg.kind === 'win';
  const prec = p.precision ?? 5;
  const unit = p.volume * p.lev;
  const dir = p.side === 'Buy' ? 1 : -1;

  const onAmount = (v) => {
    setAmount(v);
    if (priceTouched) { return; }
    const n = Math.abs(parseFloat(v));
    if (!n || !unit) { setPrice(''); return; }
    const pnl = isWin ? n : -n;
    setPrice((p.open_price + dir * pnl / unit).toFixed(prec));
  };
  const onPrice = (v) => {
    setPrice(v);
    setPriceTouched(v !== '');
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        const s = sl === '' ? 0 : parseFloat(sl);
        const t = tp === '' ? 0 : parseFloat(tp);
        if (Number.isNaN(s) || Number.isNaN(t) || s < 0 || t < 0) { toast({ title: 'Enter valid prices' }); setBusy(false); return; }
        await adminAPI.editPosition(p.id, { sl: s, tp: t });
        toast({ title: 'Stop Loss / Take Profit updated' });
      } else {
        const n = parseFloat(amount);
        const c = parseFloat(price);
        if (!n || Number.isNaN(n) || n <= 0) { toast({ title: 'Enter an amount greater than 0' }); setBusy(false); return; }
        if (Number.isNaN(c) || c <= 0) { toast({ title: 'Enter a close price above 0' }); setBusy(false); return; }
        const pnl = isWin ? n : -n;
        await adminAPI.settlePosition(p.id, pnl, c);
        toast({ title: 'Trade closed', description: `Closed at ${c} · ${money(pnl)} to ${p.user_email}` });
      }
      onDone();
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || 'Try again' });
    }
    setBusy(false);
  };

  const inputCls = 'w-full mt-1 px-3 py-2 rounded-lg bg-[#06100d] border border-[#1a2823] text-white mono text-sm focus:outline-none focus:border-[#00e676]/60';
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-sm p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]" data-testid="live-trade-dialog">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-bold">
            {isEdit ? 'Edit Stop Loss / Take Profit' : isWin ? 'Force close in profit' : 'Force close in loss'}
          </h3>
          <button onClick={onClose} data-testid="live-trade-dialog-close" className="text-gray-400 hover:text-white"><X size={16} /></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">{p.display || p.symbol} · {p.side} {p.volume} @ {p.open_price} · {p.user_email}</p>
        {isEdit ? (
          <div className="space-y-3">
            <label className="block text-xs text-gray-400">Stop Loss price (0 = none)
              <input type="number" step="any" value={sl} onChange={e => setSl(e.target.value)} data-testid="live-trade-sl-input" className={inputCls} />
            </label>
            <label className="block text-xs text-gray-400">Take Profit price (0 = none)
              <input type="number" step="any" value={tp} onChange={e => setTp(e.target.value)} data-testid="live-trade-tp-input" className={inputCls} />
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs text-gray-400">{isWin ? 'Profit' : 'Loss'} amount ($) booked to the user's balance
              <input type="number" step="any" min="0" autoFocus value={amount} onChange={e => onAmount(e.target.value)} data-testid="live-trade-amount-input" className={inputCls} />
            </label>
            <label className="block text-xs text-gray-400">Close price shown in the user's history
              <input type="number" step="any" value={price} onChange={e => onPrice(e.target.value)} data-testid="live-trade-close-price-input" className={inputCls} />
            </label>
            <p className="text-[10px] text-gray-500">Typing the amount suggests a close price. You can change either box freely before closing. Live price: {fmt(p.current, prec)} · Leverage {p.lev}x</p>
          </div>
        )}
        <button onClick={submit} disabled={busy} data-testid="live-trade-dialog-submit"
          className={`mt-5 w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 ${isEdit ? 'bg-[#00e676] text-black' : isWin ? 'bg-[#00e676] text-black' : 'bg-red-500 text-white'}`}>
          {isEdit ? <Save size={14} /> : isWin ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
          {busy ? 'Working…' : isEdit ? 'Save levels' : 'Close trade'}
        </button>
      </div>
    </div>
  );
};

const TradeRow = ({ p, onAction }) => {
  const up = p.pnl >= 0;
  const btn = 'p-1.5 rounded border border-[#1a2823] transition-colors';
  return (
    <tr className="border-t border-[#141f1b] hover:bg-white/[0.02]" data-testid={`live-trade-row-${p.id}`}>
      <td className="py-2.5 pl-3"><div className="text-white text-xs font-medium">{p.user_name || '—'}</div><div className="text-[10px] text-gray-500">{p.user_email}</div></td>
      <td className="text-white font-semibold">{p.display || p.symbol}</td>
      <td className={p.side === 'Buy' ? 'text-[#00e676]' : 'text-red-400'}>{p.side}</td>
      <td className="mono text-gray-300">{p.volume}</td>
      <td className="mono text-gray-300">{p.lev}x</td>
      <td className="mono text-gray-300">{fmt(p.open_price, p.precision ?? 2)}</td>
      <td className="mono text-white" data-testid={`live-trade-price-${p.id}`}>{fmt(p.current, p.precision ?? 2)}</td>
      <td className="mono text-gray-400">{p.sl || '—'}</td>
      <td className="mono text-gray-400">{p.tp || '—'}</td>
      <td className={`mono font-semibold ${up ? 'text-[#00e676]' : 'text-red-400'}`} data-testid={`live-trade-pnl-${p.id}`}>{money(p.pnl)}</td>
      <td className="text-right pr-3 whitespace-nowrap">
        <button onClick={() => onAction('edit', p)} data-testid={`live-trade-edit-${p.id}`} className={`${btn} mr-1 px-2 text-[10px] text-gray-300 hover:text-white`}>SL / TP</button>
        <button onClick={() => onAction('win', p)} data-testid={`live-trade-win-${p.id}`} title="Force close in profit" className={`${btn} mr-1 text-[#00e676] hover:border-[#00e676]/50`}><TrendingUp size={12} /></button>
        <button onClick={() => onAction('loss', p)} data-testid={`live-trade-loss-${p.id}`} title="Force close in loss" className={`${btn} text-red-400 hover:border-red-500/50`}><TrendingDown size={12} /></button>
      </td>
    </tr>
  );
};

export default function AdminLiveTrades() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dlg, setDlg] = useState(null);

  const load = async () => {
    try { setPositions(await adminAPI.allPositions()); } catch {}
    setLoading(false);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const prices = useLivePrices([...new Set(positions.map(p => p.symbol))]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return positions
      .filter(p => !term || [p.user_email, p.user_name, p.symbol, p.display].some(v => (v || '').toLowerCase().includes(term)))
      .map(p => {
        const current = prices[p.symbol];
        const lev = p.leverage || p.user_leverage || 1;
        if (current == null) { return { ...p, current: null, lev, pnl: null }; }
        const diff = p.side === 'Buy' ? current - p.open_price : p.open_price - current;
        return { ...p, current, lev, pnl: diff * p.volume * lev };
      });
  }, [positions, prices, q]);

  const total = rows.reduce((a, p) => a + (p.pnl || 0), 0);
  const traders = new Set(rows.map(p => p.user_id)).size;

  return (
    <div className="space-y-6" data-testid="admin-live-trades-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Activity size={20} className="text-[#00e676]" /> Live Trades</h1>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><RefreshCw size={11} /> Running trades across all users · refreshes every 5s</p>
        </div>
        <div className="relative w-full sm:w-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search user or symbol" data-testid="live-trades-search"
            className="pl-8 pr-3 py-2 w-full sm:w-64 rounded-lg bg-[#0a1210] border border-[#1a2823] text-sm text-white focus:outline-none focus:border-[#00e676]/60" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[['Open trades', rows.length, 'live-trades-count'], ['Traders', traders, 'live-trades-traders'],
          ['Floating P&L', money(total), 'live-trades-total-pnl']].map(([l, v, id]) => (
          <div key={id} className="p-4 rounded-xl border border-[#1a2823] bg-[#0a1210]">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{l}</div>
            <div data-testid={id} className={`mono text-xl font-bold mt-1 ${id === 'live-trades-total-pnl' ? (total >= 0 ? 'text-[#00e676]' : 'text-red-400') : 'text-white'}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-x-auto">
        {loading && <div className="py-12 text-center text-sm text-gray-500">Loading…</div>}
        {!loading && rows.length === 0 && <div className="py-12 text-center text-sm text-gray-500" data-testid="live-trades-empty">No running trades</div>}
        {!loading && rows.length > 0 && (
          <table className="w-full text-xs min-w-[900px]">
            <thead><tr className="text-left text-[10px] text-gray-500 uppercase">
              <th className="py-2.5 pl-3">User</th><th>Symbol</th><th>Side</th><th>Volume</th><th>Leverage</th><th>Entry</th><th>Current</th><th>SL</th><th>TP</th><th>Live P&L</th><th className="text-right pr-3">Actions</th>
            </tr></thead>
            <tbody>{rows.map(p => <TradeRow key={p.id} p={p} onAction={(kind, pos) => setDlg({ kind, pos })} />)}</tbody>
          </table>
        )}
      </div>

      {dlg && <ActionDialog dlg={{ ...dlg, pos: rows.find(r => r.id === dlg.pos.id) || dlg.pos }} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
    </div>
  );
}
